import React, { useContext, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { MSpinner } from "components/Shared";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import IconButton from "components/Shared/IconButton";
import { TopicResponse } from "requests/cms/interfaces";
import { retrieveTopic, retrieveTopicByKey, retrieveTopicMarkup, retrieveTopicMarkupByKey } from "requests/cms";
import { getHelpTopicSession, getHelpTopicMarkupSession, getHelpTopicByKeySession, getHelpTopicMarkupByKeySession} from "requests/Forms4";
import { phoenixProjectId } from "./ContentManagementList";
import MarkdownEditor from "components/Shared/MarkdownEditor";
import { stopPropagationPreventDefault } from "tools/clickEvents";
import { CONTENT_LIBRARY_VIEW } from "typeConstants/rights";
import { userHasRight } from "selectors/rights";
import { PhoenixFormsContext } from "components/PhoenixFormsV4/PhoenixFormsProvider";

type TopicIdProps = {
  topicId: string;
  topicKey?: never;
};

type TopicKeyProps = {
  topicId?: never;
  topicKey: string;
};

type Props = (TopicIdProps | TopicKeyProps) & {
  fontSize?: number;
  // From redux
  hasCMSViewRight: boolean;
};

const CMSHelpIcon = ({ topicId, topicKey, hasCMSViewRight, fontSize }: Props) => {
  const mounted = useRef(true);
  const [topic, setTopic] = useState<TopicResponse>();
  const [markup, setMarkup] = useState<string>();
  const [blockNextDialog, setBlockNextDialog] = useState<boolean>(false);
  const dialogContext = useContext<DialogProviderValue>(DialogContext);
  const phoenixFormsContext = useContext(PhoenixFormsContext);

  const webRuntimeAppData = phoenixFormsContext?.getWebRuntimeAppData();

  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    if (topic && (markup || markup === "")) showTopicDialog();
  }, [topic, markup]);

  const openNewWindow = () => {
    let newWindow = window.open();
    newWindow.document.writeln(`
<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8">
    <title>${topic.name}</title>
  </head>
  <body>
    ${markup}
  </body>
</html>`);
    newWindow.document.close();
  };

  const showTopicDialog = () => {
    if (!blockNextDialog) {
      dialogContext.show(topic.name, <MarkdownEditor compiled={markup} />, [
        { label: "Open in New Window", onClick: openNewWindow },
        { label: "Close", isPrimary: true },
      ], { className: "cms-help-dialog" });
    }
    setBlockNextDialog(false);
  };

  const cancelLoading = () => {
    setBlockNextDialog(true);
    dialogContext.hide();
  };

  const showLoadingDialog = () => {
    dialogContext.showOk("Loading Content...", <MSpinner />, cancelLoading, "Cancel", { className: "cms-help-dialog" });
  };

  const onShowHelp = () => {
    if (topic && markup) {
      showTopicDialog();
    } else {
      showLoadingDialog();

      // This component is used in the form view and form designer preview. Depending on the context, it's authenticated
      // either by session or token.
      if (webRuntimeAppData?.sessionKey) {
        if (topicId) {
          getHelpTopicSession(topicId, webRuntimeAppData?.host, webRuntimeAppData?.sessionKey, setTopic, mounted);
          getHelpTopicMarkupSession(topicId, webRuntimeAppData?.host, webRuntimeAppData?.sessionKey, markupResponse => setMarkup(markupResponse), mounted);
        } else if (topicKey) {
          getHelpTopicByKeySession(topicKey, webRuntimeAppData?.host, webRuntimeAppData?.sessionKey, setTopic, mounted);
          getHelpTopicMarkupByKeySession(topicKey, webRuntimeAppData?.host, webRuntimeAppData?.sessionKey, markupResponse => setMarkup(markupResponse), mounted);
        }
      } else {
        if (topicId) {
          retrieveTopic(phoenixProjectId, topicId, setTopic, mounted);
          retrieveTopicMarkup(topicId, markupResponse => setMarkup(markupResponse.markup), mounted);
        } else if (topicKey) {
          retrieveTopicByKey(phoenixProjectId, topicKey, setTopic, mounted);
          retrieveTopicMarkupByKey(phoenixProjectId, topicKey, markupResponse => setMarkup(markupResponse.markup), mounted);
        }
      }
    }
  };

  return hasCMSViewRight && <IconButton icon="help" onClick={stopPropagationPreventDefault(onShowHelp)} fontSize={fontSize} />;
};

const mapStateToProps = state => ({
  hasCMSViewRight: userHasRight(CONTENT_LIBRARY_VIEW)(state),
});

export default connect(mapStateToProps)(CMSHelpIcon);
